import type { ReactElement } from "react";
import { Button } from "@playbookai/ui";

/**
 * The one page control the Teams screens use, so all four lists offer the same
 * thing in the same words.
 *
 * NAMED, not generic. Three of these can be on screen at once (the roster, the
 * owner's queue, the reader's own), and "Load more" three times over says
 * nothing about which list grows — so the label carries what is being loaded,
 * and the accessible name does too.
 *
 * RENDERS NOTHING when there is no next page. A disabled control at the end of
 * a complete list implies there is more and the reader may not have it; absence
 * is the honest shape, and the count beside the list is what says how much has
 * been seen.
 *
 * This is the module-only footer the plan names (ProgressiveListFooter's
 * inline layout with a caller-owned count), built from the design-system
 * Button. The DS ProgressiveListFooter itself draws a top rule, its own count
 * and an outline md button, a look change no PR 3 row approves.
 */
export function LoadMore({
  label,
  hasMore,
  isLoadingMore,
  failed = false,
  onLoadMore,
}: {
  /** Plural, lower case, completing "Load more …" and "Loading more …". */
  label: string;
  hasMore: boolean;
  isLoadingMore: boolean;
  /**
   * The last next-page request failed. SAID BESIDE THE ROWS, the way the
   * design system's `ProgressiveListFooter` says it: the loaded rows stay on
   * screen (a failed page must not blank the list), so without this a failure
   * looked exactly like a Load more that did nothing.
   */
  failed?: boolean;
  onLoadMore: () => void;
}): ReactElement | null {
  if (!hasMore) return null;
  const showFailure = failed && !isLoadingMore;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {showFailure ? (
        <span role="alert" className="text-body-small text-pb-red-text">
          {`More ${label} could not be loaded.`}
        </span>
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        disabled={isLoadingMore}
        onClick={onLoadMore}
      >
        {isLoadingMore
          ? `Loading more ${label}…`
          : showFailure
            ? `Try loading more ${label} again`
            : `Load more ${label}`}
      </Button>
    </span>
  );
}
